import * as runtime from '../runtime';
import type {
  CitySlugResponse,
  SearchCityByNameResponseInner,
  StreetSlugResponse,
} from '../models';

export interface GetCityBySlugRequest {
  citySlugId: string;
}

export interface GetStreetBySlugRequest {
  streetSlugId: string;
  cityId: string;
}

export interface SearchCityByNameRequest {
  name: string;
}

/**
 *
 */
export class GisApi extends runtime.BaseAPI {
  async getCityBySlugRaw(
    requestParameters: GetCityBySlugRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<CitySlugResponse>> {
    if (
      requestParameters.citySlugId === null ||
      requestParameters.citySlugId === undefined
    ) {
      throw new runtime.RequiredError(
        'citySlugId',
        'Required parameter requestParameters.citySlugId was null or undefined when calling getCityBySlug.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/locations/slugs/{citySlugId}`.replace(
          `{${'citySlugId'}}`,
          encodeURIComponent(String(requestParameters.citySlugId)),
        ),
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Gets the city details for the given city slug
   */
  async getCityBySlug(
    requestParameters: GetCityBySlugRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<CitySlugResponse> {
    const response = await this.getCityBySlugRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async getStreetBySlugRaw(
    requestParameters: GetStreetBySlugRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<StreetSlugResponse>> {
    if (
      requestParameters.streetSlugId === null ||
      requestParameters.streetSlugId === undefined
    ) {
      throw new runtime.RequiredError(
        'streetSlugId',
        'Required parameter requestParameters.streetSlugId was null or undefined when calling getStreetBySlug.',
      );
    }

    if (
      requestParameters.cityId === null ||
      requestParameters.cityId === undefined
    ) {
      throw new runtime.RequiredError(
        'cityId',
        'Required parameter requestParameters.cityId was null or undefined when calling getStreetBySlug.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    if (requestParameters.cityId !== undefined) {
      queryParameters['cityId'] = requestParameters.cityId;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/gis/slugs/{streetSlugId}`.replace(
          `{${'streetSlugId'}}`,
          encodeURIComponent(String(requestParameters.streetSlugId)),
        ),
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Gets the street details for the given street slug
   */
  async getStreetBySlug(
    requestParameters: GetStreetBySlugRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<StreetSlugResponse> {
    const response = await this.getStreetBySlugRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async searchCityByNameRaw(
    requestParameters: SearchCityByNameRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<Array<SearchCityByNameResponseInner>>> {
    if (
      requestParameters.name === null ||
      requestParameters.name === undefined
    ) {
      throw new runtime.RequiredError(
        'name',
        'Required parameter requestParameters.name was null or undefined when calling searchCityByName.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/locations/search/{name}`.replace(
          `{${'name'}}`,
          encodeURIComponent(String(requestParameters.name)),
        ),
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Search the city details for the given city name
   */
  async searchCityByName(
    requestParameters: SearchCityByNameRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<Array<SearchCityByNameResponseInner>> {
    const response = await this.searchCityByNameRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }
}
